import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultInclinationFactors = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const { nu, hb, hl, normal_v, alpha1, alpha2 } = result.input;
  const {
    canUserSeeCalculationSteps,
    phips,
    HMax,
    isInclinedLoading,
    isPhiZero,
    effectiveFndArea,
    ca,
    nq,
    ic,
    icb,
    icl,
    iq,
    iqb,
    iql,
    iGamma,
    iGammab,
    iGammal,
  } = result.output;

  const case1 = isInclinedLoading && isPhiZero;
  const case2 = isInclinedLoading && !isPhiZero;
  const case3 = !isInclinedLoading && isPhiZero;
  const case4 = !isInclinedLoading && !isPhiZero;
  const caseLegendItems = [
    { label: 'CASE 1', description: 'Inclined loading with \u03c6 = 0', isActive: case1 },
    { label: 'CASE 2', description: 'Inclined loading with \u03c6 \u2260 0', isActive: case2 },
    { label: 'CASE 3', description: 'No inclined loading with \u03c6 = 0', isActive: case3 },
    { label: 'CASE 4', description: 'No inclined loading with \u03c6 \u2260 0', isActive: case4 },
  ];
  const [showCase1IcDetails, setShowCase1IcDetails] = useState(false);
  const [showCase2IqDetails, setShowCase2IqDetails] = useState(false);
  const [showCase2IcDetails, setShowCase2IcDetails] = useState(false);
  const [showCase2IgammaDetails, setShowCase2IgammaDetails] = useState(false);
  const [showCase3IcDetails, setShowCase3IcDetails] = useState(false);
  const [showCase4IqDetails, setShowCase4IqDetails] = useState(false);
  const [showCase4IcDetails, setShowCase4IcDetails] = useState(false);
  const [showCase4IgammaDetails, setShowCase4IgammaDetails] = useState(false);
  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const Case1Ic = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i'<sub>c</sub>
        </h4>
        <p>
          &phi; = 0 &rarr; Only i'<sub>c</sub> should be calculated
        </p>
        <p>
          i'<sub>c</sub> = 0.5 - (1 - H<sub>i</sub> / (A<sub>f</sub>C<sub>a</sub>))<sup>1/2</sup>
        </p>
        <p>
          i'<sub>c</sub> = 0.5 - (1 - {round(HMax, 2)} / [({round(effectiveFndArea, 2)})({round(ca, 2)})])<sup>1/2</sup>
        </p>
        <p>
          i'<sub>c</sub> = {round(ic, 2)}
        </p>
      </div>
    );
  };

  const Case2Iq = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>q</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          i<sub>q, B</sub> and i<sub>q, L</sub> should be calculated.
        </p>
        <br />
        <p>
          i<sub>q, B</sub>= [1 - 0.5H<sub>B</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
          <sup>
            &alpha;<sub>1</sub>
          </sup>
        </p>
        <p>
          i<sub>q, B</sub>= [1 - (0.5){round(hb, 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)})(
          {round(ca, 2)})(cot{round(phips, 2)}))]
          <sup>{round(alpha1, 2)}</sup>
        </p>
        <p>
          i<sub>q, B</sub>={round(iqb, 2)}
        </p>
        <br />
        <p>
          i<sub>q, L</sub>= [1 - 0.5H<sub>L</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
          <sup>
            &alpha;<sub>1</sub>
          </sup>
        </p>
        <p>
          i<sub>q, L</sub>= [1 - (0.5){round(hl, 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)})(
          {round(ca, 2)})(cot{round(phips, 2)}))]
          <sup>{round(alpha1, 2)}</sup>
        </p>
        <p>
          i<sub>q, L</sub>={round(iql, 2)}
        </p>
      </div>
    );
  };
  const Case2Ic = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>c</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          i<sub>c, B</sub> and i<sub>c, L</sub> should be calculated.
        </p>
        <br />
        <p>
          i<sub>c, B</sub> = i<sub>q, B</sub> - (1 - i<sub>q, B</sub>) / (N<sub>q</sub> - 1)
        </p>
        <p>
          i<sub>c, B</sub> = {round(iqb, 2)} - (1 - {round(iqb, 2)}) / ({round(nq, 2)} - 1)
        </p>
        <p>
          i<sub>c, B</sub>={round(icb, 2)}
        </p>
        <br />
        <p>
          i<sub>c, L</sub> = i<sub>q, L</sub> - (1 - i<sub>q, L</sub>) / (n<sub>q</sub> - 1)
        </p>
        <p>
          i<sub>c, L</sub> = {round(iql, 2)} - (1 - {round(iql, 2)}) / ({round(nq, 2)} - 1)
        </p>
        <p>
          i<sub>c, L</sub>={round(icl, 2)}
        </p>
      </div>
    );
  };
  const Case2Igamma = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>&gamma;</sub>
        </h4>
        <p>
          H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
        </p>
        <p>
          i<sub>&gamma;, B</sub> and i<sub>&gamma;, L</sub> should be calculated.
        </p>
        <br />
        <p>
          i<sub>&gamma;, B</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>B</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
          cot&phi;)&#125;
          <sup>
            &alpha;<sub>2</sub>
          </sup>
        </p>
        <p>
          i<sub>&gamma;, B</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(hb, 2)}] / ({round(normal_v, 2)} + (
          {round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)})&#125;<sup>{round(alpha2, 2)}</sup>
        </p>
        <p>
          i<sub>&gamma;, B</sub> = {round(iGammab, 2)}
        </p>
        <br />
        <p>
          i<sub>&gamma;, L</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>L</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
          cot&phi;)&#125;
          <sup>
            &alpha;<sub>2</sub>
          </sup>
        </p>
        <p>
          i<sub>&gamma;, L</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(hl, 2)}] / ({round(normal_v, 2)} + (
          {round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)})&#125;<sup>{round(alpha2, 2)}</sup>
        </p>
        <p>
          i<sub>&gamma;, L</sub> = {round(iGammal, 2)}
        </p>
      </div>
    );
  };

  const Case3Ic = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i'<sub>c</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.) and &phi; = 0
        </p>
        <p>&darr;</p>
        <p>
          i'<sub>c</sub> = 0
        </p>
      </div>
    );
  };

  const Case4Iq = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>q</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          i<sub>q</sub>= [1 - 0.5H<sub>i</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
          <sup>
            &alpha;<sub>1</sub>
          </sup>
        </p>
        <p>
          i<sub>q</sub>= [1 - (0.5){round(Math.max(hb, hl), 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)})(
          {round(ca, 2)})(cot{round(phips, 2)}))]
          <sup>{round(alpha1, 2)}</sup>
        </p>
        <p>
          i<sub>q</sub>={round(iq, 2)}
        </p>
      </div>
    );
  };
  const Case4Ic = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>c</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          i<sub>c</sub> = i<sub>q</sub> - (1 - i<sub>q</sub>) / (N<sub>q</sub> - 1)
        </p>
        <p>
          i<sub>c</sub> = {round(iq, 2)} - (1 - {round(iq, 2)}) / ({round(nq, 2)} - 1)
        </p>

        <p>
          i<sub>c</sub> = {round(ic, 2)}
        </p>
      </div>
    );
  };
  const Case4Igamma = () => {
    return (
      <div className="meyerhof-factors">
        <h4>
          i<sub>&gamma;</sub>
        </h4>
        <p>
          H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
        </p>
        <p>
          i<sub>&gamma;</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>i</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
          cot&phi;)&#125;<sup>{round(alpha2, 2)}</sup>
        </p>
        <p>
          i<sub>&gamma;</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(Math.max(hl, hb), 2)}] / ({round(normal_v, 2)} + (
          {round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)})&#125;<sup>{round(alpha2, 2)}</sup>
        </p>
        <p>
          i<sub>&gamma;</sub> = {round(iGamma, 2)}
        </p>
      </div>
    );
  };

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Hansen Inclination Factors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes Hansen inclination factors (i<sub>c</sub>, i<sub>q</sub>, and i<sub>&gamma;</sub>) based on load
          inclination and friction-angle conditions. The 4 cases that can happen based on these conditions are listed below:
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Case Legend
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
            {caseLegendItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.7,
                  backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                }}
              >
                {renderCaseBadge(item.label, {
                  ml: 0,
                  minWidth: 64,
                  justifyContent: 'center',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  color: item.isActive ? 'success.dark' : 'text.disabled',
                  backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                  fontWeight: item.isActive ? 700 : 600,
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Case 1 i'c Factor */}
          {case1 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase1IcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase1IcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factor (i'<sub>c</sub>) = {round(ic, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase1IcDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    &phi; = 0 &rarr; Only i'<sub>c</sub> should be calculated
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i'<sub>c</sub> = 0.5 - (1 - H<sub>i</sub> / (A<sub>f</sub>C<sub>a</sub>))<sup>1/2</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i'<sub>c</sub> = 0.5 - (1 - {round(HMax, 2)} / [({round(effectiveFndArea, 2)})({round(ca, 2)})])
                    <sup>1/2</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i'<sub>c</sub> = {round(ic, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 2 iq Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase2IqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase2IqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factors &rarr; i<sub>q,B</sub> = {round(iqb, 2)}, i<sub>q,L</sub> = {round(iql, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2IqDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, B</sub> and i<sub>q, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, B</sub>= [1 - 0.5H<sub>B</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
                    <sup>
                      &alpha;<sub>1</sub>
                    </sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, B</sub>= [1 - (0.5){round(hb, 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)})(
                    {round(ca, 2)})(cot{round(phips, 2)}))]<sup>{round(alpha1, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, B</sub>={round(iqb, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, L</sub>= [1 - 0.5H<sub>L</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
                    <sup>
                      &alpha;<sub>1</sub>
                    </sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q, L</sub>= [1 - (0.5){round(hl, 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)})(
                    {round(ca, 2)})(cot{round(phips, 2)}))]<sup>{round(alpha1, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>q, L</sub>={round(iql, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 2 ic Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase2IcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase2IcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factors &rarr; i<sub>c,B</sub> {round(icb, 2)}, i<sub>c,L</sub> {round(icl, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2IcDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, B</sub> and i<sub>c, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, B</sub> = i<sub>q, B</sub> - (1 - i<sub>q, B</sub>) / (N<sub>q</sub> - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, B</sub> = {round(iqb, 2)} - (1 - {round(iqb, 2)}) / ({round(nq, 2)} - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, B</sub>={round(icb, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, L</sub> = i<sub>q, L</sub> - (1 - i<sub>q, L</sub>) / (n<sub>q</sub> - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c, L</sub> = {round(iql, 2)} - (1 - {round(iql, 2)}) / ({round(nq, 2)} - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>c, L</sub>={round(icl, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 2 i_gamma Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase2IgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase2IgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factors &rarr; i<sub>&gamma;,B</sub> {round(iGammab, 2)}, i<sub>&gamma;,L</sub> {round(iGammal, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2IgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, B</sub> and i<sub>&gamma;, L</sub> should be calculated.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, B</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>B</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
                    cot&phi;)&#125;
                    <sup>
                      &alpha;<sub>2</sub>
                    </sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, B</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(hb, 2)}] / ({round(normal_v, 2)} + (
                    {round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)})&#125;<sup>{round(alpha2, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, B</sub> = {round(iGammab, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, L</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>L</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
                    cot&phi;)&#125;
                    <sup>
                      &alpha;<sub>2</sub>
                    </sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;, L</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(hl, 2)}] / ({round(normal_v, 2)} + (
                    {round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)})&#125;<sup>{round(alpha2, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>&gamma;, L</sub> = {round(iGammal, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 3 i'c Factor */}
          {case3 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase3IcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase3IcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factor (i'<sub>c</sub>) = 0
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase3IcDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.) and &phi; = 0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    &darr;
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i'<sub>c</sub> = 0
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 4 iq Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase4IqDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase4IqDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factor (i<sub>q</sub>) = {round(iq, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4IqDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q</sub>= [1 - 0.5H<sub>i</sub> / (V + A<sub>f</sub>C<sub>a</sub>cot&phi;)]
                    <sup>
                      &alpha;<sub>1</sub>
                    </sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>q</sub>= [1 - (0.5){round(Math.max(hb, hl), 2)} / ({round(normal_v, 2)} + ({round(effectiveFndArea, 2)}
                    )(
                    {round(ca, 2)})(cot{round(phips, 2)}))]
                    <sup>{round(alpha1, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>q</sub>={round(iq, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 4 ic Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase4IcDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase4IcDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factor (i<sub>c</sub>) = {round(ic, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4IcDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c</sub> = i<sub>q</sub> - (1 - i<sub>q</sub>) / (N<sub>q</sub> - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>c</sub> = {round(iq, 2)} - (1 - {round(iq, 2)}) / ({round(nq, 2)} - 1)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>c</sub> = {round(ic, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
          {/* Success Alert: Case 4 i_gamma Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCase4IgammaDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCase4IgammaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Inclination factor (i<sub>&gamma;</sub>) = {round(iGamma, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4IgammaDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;</sub> = &#123; 1 - [(0.7 - &eta;/450)H<sub>i</sub>] / (V + A<sub>f</sub>C<sub>a</sub>
                    cot&phi;)&#125;<sup>{round(alpha2, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    i<sub>&gamma;</sub> = &#123; 1 - [(0.7 - {round(nu, 2)}/450){round(Math.max(hl, hb), 2)}] / (
                    {round(normal_v, 2)} + ({round(effectiveFndArea, 2)}) ({round(ca, 2)}) cot{round(phips, 2)}) &#125;
                    <sup>{round(alpha2, 2)}</sup>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    i<sub>&gamma;</sub> = {round(iGamma, 2)}
                  </Typography>
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultInclinationFactors;
